/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Import component UI sổ mà bạn đã có (Đảm bảo file này nằm đúng đường dẫn)
import OwnerEquityDetailLedgerDetail from "./info/index";

import { DetailedLedgerOwnersCapitalService } from "services/detailed-ledger-owners-capital";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

// Initial values chuẩn OwnerEquityDetailLedger schema
const createEquityValues = () => ({
  contributedCapital: "",
  sharePremium: "",
  bondConversionOption: "",
  otherCapital: "",
});

const initialValues = {
  companyAddress: "",
  year: today.getFullYear(),
  openingBalance: createEquityValues(),
  totalArisingDebit: createEquityValues(),
  totalArisingCredit: createEquityValues(),
  closingBalance: createEquityValues(),
  totalPages: "",
  openingDate: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
  preparerName: "",
  chiefAccountantName: "",
  legalRepresentativeName: "",
  status: "OPEN",
};

function DetailedLedgerOwnersCapitalDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [getData, setGetData] = useState({});
  const [data, setData] = useState({});

  const [formInitials, setFormInitials] = useState(initialValues);

  // Load data từ API và map vào form
  useEffect(() => {
    if (!id) return;

    const loadDetail = async () => {
      try {
        const response = await DetailedLedgerOwnersCapitalService.getById(id);
        const apiData = response.data || response;

        // Helper parse OwnerEquityValues thành flat form values
        const parseEquityValues = (equity) => ({
          contributedCapital: equity?.contributedCapital?.toString() || "",
          sharePremium: equity?.sharePremium?.toString() || "",
          bondConversionOption: equity?.bondConversionOption?.toString() || "",
          otherCapital: equity?.otherCapital?.toString() || "",
        });

        // Parse ngày ký
        const parseSigningDate = (dateStr) => {
          if (!dateStr) return { day: "", month: "", year: "" };
          const date = new Date(dateStr);
          return {
            day: date.getDate().toString(),
            month: (date.getMonth() + 1).toString(),
            year: date.getFullYear().toString(),
          };
        };

        const signing = parseSigningDate(apiData.signingDate);

        const loadedData = {
          company: apiData.company?.name || "",
          companyAddress: apiData.companyAddress || apiData.company?.address || "",
          year: apiData.year || today.getFullYear(),
          openingBalance: parseEquityValues(apiData.openingBalance),
          totalArisingDebit: parseEquityValues(apiData.totalArisingDebit),
          totalArisingCredit: parseEquityValues(apiData.totalArisingCredit),
          closingBalance: parseEquityValues(apiData.closingBalance),
          totalPages: apiData.totalPages?.toString() || "",
          openingDate: apiData.openingDate
            ? new Date(apiData.openingDate).toLocaleDateString("vi-VN")
            : "",
          signingDay: signing.day,
          signingMonth: signing.month,
          signingYear: signing.year,
          preparerName: apiData.preparerName || "",
          chiefAccountantName: apiData.chiefAccountantName || "",
          legalRepresentativeName: apiData.legalRepresentativeName || "",
          status: apiData.status || "OPEN",
        };

        // IMPORTANT: Component info/index.js cần items array để render table
        // Backend trả items là OwnerEquityTransaction[] với nested debit/credit/balance
        loadedData.items = (apiData.items || []).map((item) => ({
          recordingDate: item.recordingDate
            ? new Date(item.recordingDate).toLocaleDateString("vi-VN")
            : "",
          documentNumber: item.documentNumber || "",
          documentDate: item.documentDate
            ? new Date(item.documentDate).toLocaleDateString("vi-VN")
            : "",
          description: item.description || "",
          correspondingAccount: item.correspondingAccount || "",
          debit: parseEquityValues(item.debit),
          credit: parseEquityValues(item.credit),
          balance: parseEquityValues(item.balance),
        }));

        setFormInitials(loadedData);
        setGetData(apiData);
      } catch (error) {
        console.error("Lỗi load Sổ Vốn Chủ Sở Hữu:", error);
        toast.error("Không thể tải dữ liệu sổ");
      }
    };

    loadDetail();
  }, [id]);

  const onChangeValue = useCallback((type, value) => {
    setData((prev) => ({ ...prev, [type]: value }));
  }, []);

  const handleBack = () => navigation(-1);

  const submitForm = async (values) => {
    try {
      const num = (val) => (val && !isNaN(val) ? Number(val) : 0);

      // Lấy data từ child component (logic tự tính trong info/index.js)
      const getValue = (field) => (data[field] !== undefined ? data[field] : values[field]);

      // Parse OwnerEquityValues flat → nested object
      const buildEquityValues = (prefix) => ({
        contributedCapital: num(getValue(`${prefix}.contributedCapital`)),
        sharePremium: num(getValue(`${prefix}.sharePremium`)),
        bondConversionOption: num(getValue(`${prefix}.bondConversionOption`)),
        otherCapital: num(getValue(`${prefix}.otherCapital`)),
      });

      // Build items array từ form data (nested debit/credit/balance)
      const items = getValue("items") || [];
      const buildItems = () =>
        items.map((item) => ({
          recordingDate: item.recordingDate || null,
          documentNumber: item.documentNumber || "",
          documentDate: item.documentDate || null,
          description: item.description || "",
          correspondingAccount: item.correspondingAccount || "",
          debit: {
            contributedCapital: num(item.debit?.contributedCapital),
            sharePremium: num(item.debit?.sharePremium),
            bondConversionOption: num(item.debit?.bondConversionOption),
            otherCapital: num(item.debit?.otherCapital),
          },
          credit: {
            contributedCapital: num(item.credit?.contributedCapital),
            sharePremium: num(item.credit?.sharePremium),
            bondConversionOption: num(item.credit?.bondConversionOption),
            otherCapital: num(item.credit?.otherCapital),
          },
          balance: {
            contributedCapital: num(item.balance?.contributedCapital),
            sharePremium: num(item.balance?.sharePremium),
            bondConversionOption: num(item.balance?.bondConversionOption),
            otherCapital: num(item.balance?.otherCapital),
          },
        }));

      // Build signingDate ISO - FIXED template literal + validation
      const signingDate = (() => {
        const day = parseInt(getValue("signingDay"));
        const month = parseInt(getValue("signingMonth"));
        const year = parseInt(getValue("signingYear"));

        if (
          !isNaN(day) &&
          !isNaN(month) &&
          !isNaN(year) &&
          day > 0 &&
          day <= 31 &&
          month > 0 &&
          month <= 12 &&
          year > 1900
        ) {
          const dateStr = `${year}-${month.toString().padStart(2, "0")}-${day
            .toString()
            .padStart(2, "0")}`;
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return date.toISOString();
          }
        }
        return new Date().toISOString(); // fallback
      })();

      const payload = {
        companyAddress: getValue("companyAddress"),
        year: num(getValue("year")),
        openingBalance: buildEquityValues("openingBalance"),
        totalArisingDebit: buildEquityValues("totalArisingDebit"),
        totalArisingCredit: buildEquityValues("totalArisingCredit"),
        closingBalance: buildEquityValues("closingBalance"),
        items: buildItems(),
        totalPages: num(getValue("totalPages")),
        openingDate: getValue("openingDate") || null,
        signingDate,
        preparerName: getValue("preparerName"),
        chiefAccountantName: getValue("chiefAccountantName"),
        legalRepresentativeName: getValue("legalRepresentativeName"),
        status: getValue("status") || "OPEN",
      };

      console.log("SỔ VỐN CHỦ SỞ HỮU - Submit payload:", JSON.stringify(payload, null, 2));

      if (!id) {
        await DetailedLedgerOwnersCapitalService.create(payload);
        toast.success("Tạo Sổ S51-DN thành công!");
      } else {
        await DetailedLedgerOwnersCapitalService.update(id, payload);
        toast.success("Cập nhật Sổ S51-DN thành công!");
      }
    } catch (error) {
      console.error("Lỗi lưu Sổ Vốn Chủ Sở Hữu:", error);
      toast.error(error?.response?.data?.message || "Lưu thất bại!");
      throw error;
    }
  };

  const handlePrintDirect = useCallback(
    (values) => {
      const finalData = { ...values, ...data, ...getData };

      const printData = {
        headerInfo: finalData,
        rows: finalData.items || [],
        totals: {
          totalArisingDebit: finalData.totalArisingDebit,
          totalArisingCredit: finalData.totalArisingCredit,
        },
        closingBalance: finalData.closingBalance,
      };

      const htmlContent = getPrintTemplate(printData);
      const printWindow = window.open("", "_blank");
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.onload = () => printWindow.print();
    },
    [data, getData]
  );

  const handleSubmit = async (values, actions) => {
    try {
      await submitForm(values);
    } catch (error) {
      // Error handled in submitForm
    } finally {
      if (actions) {
        actions.setTouched({});
        actions.setSubmitting(false);
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center" sx={{ height: "100%" }}>
          <Grid item xs={12} lg={12}>
            <Formik initialValues={formInitials} enableReinitialize={true} onSubmit={handleSubmit}>
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <SoftBox>
                        {/* COMPONENT GIAO DIỆN SỔ CHI TIẾT */}
                        <OwnerEquityDetailLedgerDetail
                          formData={{
                            values,
                            onChangeValue,
                            getData,
                          }}
                        />

                        <SoftBox mt={10} width="100%" display="flex" justifyContent="space-between">
                          <SoftButton variant="gradient" color="light" onClick={handleBack}>
                            Trở về
                          </SoftButton>

                          {containView ? (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                variant="gradient"
                                color="success"
                                onClick={() => handlePrintDirect(values)}
                                type="button"
                              >
                                In Sổ S51-DN
                              </SoftButton>
                            </SoftBox>
                          ) : (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                disabled={isSubmitting}
                                type="submit"
                                variant="gradient"
                                color="dark"
                              >
                                {id ? "Lưu Sổ" : "Tạo Sổ Mới"}
                              </SoftButton>
                            </SoftBox>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default DetailedLedgerOwnersCapitalDetail;
